import { toast } from "sonner";
import { Users, Wallet, TrendingUp, Hourglass } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { fmtEur } from "@/lib/crm";

const ACCESS = { admin: ["Admin", "bg-slate-900 text-white"], active: ["Attivo", "bg-emerald-100 text-emerald-700"], readonly: ["Sola lettura", "bg-amber-100 text-amber-700"], blocked: ["Bloccato", "bg-rose-100 text-rose-700"], none: ["Non abbonato", "bg-slate-100 text-slate-600"] };
const STATUS = { trialing: "In prova", active: "Pagante", past_due: "Pagamento fallito", unpaid: "Non pagato", canceled: "Disdetto", comp: "Omaggio" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

const Kpi = ({ icon: I, label, value, id }) => (
  <div className="rounded-xl bg-white border border-slate-200/80 p-5" data-testid={id}>
    <I className="h-5 w-5 text-sky-600" />
    <p className="text-xs uppercase tracking-wide text-slate-500 mt-3">{label}</p>
    <p className="font-display text-2xl font-bold mt-1">{value}</p>
  </div>
);

export default function Admin() {
  const { data, error } = useApi("/admin/overview");
  const refresh = useRefresh();
  if (error) return <p className="text-rose-600" data-testid="admin-forbidden">Accesso riservato all'amministratore.</p>;
  if (!data) return <p className="text-slate-400">Caricamento…</p>;
  const comp = async (u, v) => {
    try { await api.post(`/admin/users/${u.user_id}/comp`, { comp: v }); toast.success(v ? "Accesso omaggio attivato" : "Accesso omaggio rimosso"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const paying = data.users.filter((u) => u.status === "active").length;
  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Pannello amministratore</h1>
        <p className="text-sm text-slate-500 mt-1">Abbonati, stati e incassi · Stripe in modalità <b data-testid="stripe-mode">{data.stripe_mode === "test" ? "test" : "live"}</b></p>
        <button data-testid="download-audit-btn" onClick={async () => {
          try {
            const r = await api.get("/admin/audit-report", { responseType: "blob" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "Kreditofacile_Audit_Privacy_GDPR_AI.md"; a.click();
          } catch { toast.error("Download non riuscito"); }
        }} className="mt-3 rounded-full border border-slate-300 px-4 py-2 text-sm hover:border-sky-500 hover:text-sky-700 transition-colors">Scarica rapporto audit privacy, GDPR e AI</button>
        <button data-testid="download-verifica-btn" onClick={async () => {
          try {
            const r = await api.get("/admin/audit-report?doc=verifica", { responseType: "blob" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "Kreditofacile_Verifica_Finale_Privacy.md"; a.click();
          } catch { toast.error("Download non riuscito"); }
        }} className="mt-3 ml-2 rounded-full border border-slate-300 px-4 py-2 text-sm hover:border-sky-500 hover:text-sky-700 transition-colors">Scarica verifica finale privacy</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi id="kpi-users" icon={Users} label="Utenti registrati" value={data.users.length} />
        <Kpi id="kpi-trialing" icon={Hourglass} label="In prova / paganti" value={`${data.trialing} / ${paying}`} />
        <Kpi id="kpi-mrr" icon={TrendingUp} label="Ricavo ricorrente mensile" value={fmtEur(data.mrr)} />
        <Kpi id="kpi-revenue" icon={Wallet} label="Incassato (mese / totale)" value={`${fmtEur(data.revenue_month)} / ${fmtEur(data.revenue_total)}`} />
      </div>
      <section className="bg-white border border-slate-200/80 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
            <tr>{["Utente", "Registrato", "Accesso", "Abbonamento", "Piano", "Fine prova / rinnovo", "Incassato", "Omaggio"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.user_id} className="border-b last:border-0" data-testid={`admin-user-${u.user_id}`}>
                <td className="px-4 py-3"><p className="font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                <td className="px-4 py-3">{fmt(u.created_at)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ACCESS[u.access]?.[1]}`}>{ACCESS[u.access]?.[0]}</span></td>
                <td className="px-4 py-3">{STATUS[u.status] || "—"}{u.cancel_at_period_end && <span className="text-xs text-amber-600"> (disdetto)</span>}</td>
                <td className="px-4 py-3">{u.plan === "yearly" ? "Annuale" : u.plan === "monthly" ? "Mensile" : "—"}</td>
                <td className="px-4 py-3">{u.status === "trialing" ? fmt(u.trial_end) : fmt(u.current_period_end)}</td>
                <td className="px-4 py-3 font-mono">{fmtEur(u.paid_total)}</td>
                <td className="px-4 py-3">{u.access !== "admin" && <Switch data-testid={`admin-comp-${u.user_id}`} checked={u.status === "comp"} onCheckedChange={(v) => comp(u, v)} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
